import { z } from 'zod';
import type { ZodError } from 'zod';

// Work Order
// Note: Only validates fields checked by the original validate() function.
// Other fields (clientName, location, urgent, billable, etc.) are not required
// by the form and pass through to the API as-is.
export const workOrderSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  clientName: z.string(),
  itemName: z.string().min(1, 'Item is required').max(255),
  qty: z.number().int().min(1, 'Quantity must be at least 1').max(9999),
  location: z.string().max(500).optional().or(z.literal('')),
  notes: z.string().max(2000).optional().or(z.literal('')),
  urgent: z.boolean(),
  billable: z.boolean(),
  billingNotes: z.string().max(1000).optional().or(z.literal('')),
  floral: z.boolean(),
  assignedTo: z.string().optional().or(z.literal('')),
  assignedToName: z.string().optional().or(z.literal('')),
});

export type WorkOrderFormData = z.infer<typeof workOrderSchema>;

// Contact (used in AddClientModal's LocalContactForm)
export const contactSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  email: z.string().email('Must be a valid email').or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  notes: z.string().max(500).optional().or(z.literal('')),
});

export type ContactFormData = z.infer<typeof contactSchema>;

// Email Template
export const emailTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required').max(100),
  description: z.string().max(500).optional().or(z.literal('')),
  subjectTemplate: z.string().min(1, 'Subject is required').max(255),
  bodyTemplate: z.string().min(1, 'Body is required').max(10000),
  isActive: z.boolean().default(true),
});

export type EmailTemplateFormData = z.infer<typeof emailTemplateSchema>;

/**
 * Converts a ZodError into a flat record of field -> first error message.
 * Compatible with existing {[key: string]: string} error state pattern.
 */
export function formatZodErrors<T>(error: ZodError<T>): Partial<Record<keyof T, string>> {
  const result: Partial<Record<keyof T, string>> = {};
  for (const issue of error.issues) {
    const key = issue.path[0] as keyof T;
    if (key && !result[key]) {
      result[key] = issue.message;
    }
  }
  return result;
}
